import { useEffect, useState, type ReactNode } from "react";
import type { UpdateLead } from "@fmc/contracts";
import { StatusBadge } from "./reporting";
import { useSession } from "../session";
import { sourceMeta, deliveryStateMeta, outcomeCatalog } from "../data/leads";
import type { Lead, Outcome, OutcomeType } from "../data/leads";

function SectionLabel({ children }: { children: string }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tertiary mb-3">
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-micro text-tertiary">{label}</span>
      <span className="text-body text-primary whitespace-pre-wrap break-words">{value}</span>
    </div>
  );
}

const editInputClass =
  "w-full h-10 px-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50 transition-colors";

function EditField({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="text-micro font-medium text-secondary">{label}</span>
      {children}
    </label>
  );
}

function resultMeta(result: string) {
  if (result === "success") return { icon: "fa-circle-check", color: "text-status-green" };
  if (result === "failed") return { icon: "fa-circle-xmark", color: "text-status-red" };
  return { icon: "fa-clock-rotate-left", color: "text-status-amber" };
}

function money(v: number) {
  return "£" + v.toLocaleString("en-GB");
}

const toneText: Record<string, string> = {
  green: "text-status-green",
  amber: "text-status-amber",
  blue: "text-status-blue",
  red: "text-status-red",
  neutral: "text-tertiary",
};

/* ---------------- Outcome composer ---------------- */

function OutcomeComposer({ onAdd, actorName }: { onAdd: (o: Outcome) => void; actorName: string }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<OutcomeType>("call_booked");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");

  const meta = outcomeCatalog[type];

  const submit = () => {
    onAdd({
      id: `OC-${Date.now()}`,
      type,
      ...(meta.hasValue && value ? { value: Number(value) } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
      at: new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      by: actorName,
    });
    setType("call_booked");
    setValue("");
    setNote("");
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-4 h-9 w-full rounded-xs border border-dashed border-default text-secondary text-body font-medium flex items-center justify-center gap-2 hover:bg-surface-light hover:text-primary transition-colors"
      >
        <i className="fa-solid fa-plus text-[12px]" />
        Add outcome
      </button>
    );
  }

  return (
    <div className="mt-4 rounded-md border border-default bg-surface-light p-3.5">
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(Object.keys(outcomeCatalog) as OutcomeType[]).map((t) => {
          const m = outcomeCatalog[t];
          const active = type === t;
          return (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-xs text-micro font-medium transition-colors ${
                active
                  ? "bg-brand-red-tint text-brand-red border border-brand-red/30"
                  : "bg-surface-white text-secondary border border-default hover:bg-surface-light"
              }`}
            >
              <i className={`fa-solid ${m.icon} text-[11px]`} />
              {m.label}
            </button>
          );
        })}
      </div>
      {meta.hasValue && (
        <div className="mb-2.5">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-body text-tertiary">
              £
            </span>
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Annual value"
              className="w-full h-9 pl-7 pr-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50"
            />
          </div>
        </div>
      )}
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Add a note (optional)"
        className="w-full h-9 px-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50"
      />
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={submit}
          className="flex-1 h-9 rounded-xs bg-brand-red text-on-brand text-body font-medium flex items-center justify-center gap-2 hover:bg-brand-red-hover transition-colors"
        >
          <i className="fa-solid fa-check text-[12px]" />
          Log outcome
        </button>
        <button
          onClick={() => setOpen(false)}
          className="h-9 px-4 rounded-xs border border-default text-secondary text-body font-medium hover:bg-surface-white transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ---------------- Outcome timeline ---------------- */

function OutcomeTimeline({ outcomes }: { outcomes: Outcome[] }) {
  if (outcomes.length === 0) {
    return (
      <p className="text-body text-tertiary italic">
        No outcomes logged yet. Add the first when this lead progresses.
      </p>
    );
  }
  return (
    <div className="flex flex-col">
      {outcomes.map((o, i) => {
        const m = outcomeCatalog[o.type];
        const last = i === outcomes.length - 1;
        return (
          <div key={o.id} className="flex gap-3">
            {/* Rail */}
            <div className="flex flex-col items-center">
              <span className="w-7 h-7 rounded-full bg-surface-white border border-default flex items-center justify-center">
                <i className={`fa-solid ${m.icon} text-[12px] ${toneText[m.tone]}`} />
              </span>
              {!last && <span className="w-px flex-1 bg-default my-1" />}
            </div>
            {/* Body */}
            <div className={`flex-1 min-w-0 ${last ? "" : "pb-4"}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-body font-medium text-primary">{m.label}</span>
                {o.value != null && (
                  <span className="text-body font-bold text-status-green">{money(o.value)}/yr</span>
                )}
              </div>
              {o.note && <div className="text-caption text-secondary mt-0.5">{o.note}</div>}
              <div className="text-micro text-tertiary mt-1">
                {o.at} · {o.by}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Drawer ---------------- */

export function LeadDrawer({
  lead,
  outcomes,
  onAddOutcome,
  onClose,
  onDelete,
  onAddNote,
  onUpdate,
}: {
  lead: Lead | null;
  outcomes: Outcome[];
  onAddOutcome: (leadId: string, outcome: Outcome) => void;
  onClose: () => void;
  onDelete?: (id: string, password: string) => Promise<void> | void;
  onAddNote?: (id: string, body: string) => Promise<void> | void;
  onUpdate?: (id: string, values: UpdateLead) => Promise<void> | void;
}) {
  const { canEditLeads, userName } = useSession();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [password, setPassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editValues, setEditValues] = useState<UpdateLead | null>(null);
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDeleteOpen(false);
    setDeleteReason("");
    setPassword("");
    setDeleteError("");
    setEditOpen(false);
    setEditValues(null);
    setEditError("");
    setSaving(false);
  }, [lead?.id]);

  if (!lead) return null;
  const src = sourceMeta[lead.source];
  const del = deliveryStateMeta[lead.delivery];

  const wonValue = outcomes
    .filter((o) => o.type === "won")
    .reduce((sum, o) => sum + (o.value ?? 0), 0);

  const startEditing = () => {
    setEditValues({
      version: lead.version,
      firstName: lead.firstName,
      lastName: lead.lastName,
      email: lead.email,
      phone: lead.phone,
      status: lead.status,
      jobTitle: lead.jobTitle,
      practice: lead.practice,
      practicePostcode: lead.practicePostcode,
      practiceAddress: lead.practiceAddress,
      practiceType: lead.practiceType,
      enquiryNotes: lead.enquiryNotes,
    });
    setEditError("");
    setEditOpen(true);
  };

  const setEditField = <FieldName extends keyof UpdateLead>(
    field: FieldName,
    value: UpdateLead[FieldName],
  ) => setEditValues((current) => (current ? { ...current, [field]: value } : current));

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-[var(--overlay-backdrop)]" onClick={onClose} />
      <div
        style={{ width: "min(640px, 96vw)" }}
        className="relative h-full bg-surface-white border-l border-default flex flex-col shadow-[var(--shadow-md)]"
      >
        {/* Header */}
        <div className="shrink-0 px-[26px] pt-[22px] pb-[18px] border-b border-default">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 mb-1.5">
                <StatusBadge label={lead.status} tone={lead.status === "SQL" ? "green" : "blue"} />
              </div>
              <h2 className="text-title-md font-bold text-primary truncate">{lead.name}</h2>
              <div className="text-body text-secondary mt-0.5">Received from FMC HubSpot</div>
            </div>
            <div className="flex items-center gap-2">
              {canEditLeads && !editOpen && (
                <button
                  onClick={startEditing}
                  aria-label="Edit lead"
                  className="h-9 px-3 shrink-0 rounded-xs border border-default text-secondary text-body font-medium flex items-center justify-center gap-2 hover:bg-surface-light transition-colors"
                >
                  <i className="fa-solid fa-pen text-[12px]" />
                  Edit
                </button>
              )}
              <button
                onClick={onClose}
                aria-label="Close lead"
                className="w-9 h-9 shrink-0 rounded-xs border border-default text-tertiary flex items-center justify-center hover:bg-surface-light transition-colors"
              >
                <i className="fa-solid fa-xmark text-[15px]" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-5 mt-4">
            <a
              href={`mailto:${lead.email}`}
              className="flex items-center gap-2 text-caption text-brand-blue hover:underline"
            >
              <i className="fa-solid fa-envelope text-[12px]" />
              {lead.email}
            </a>
            <a
              href={lead.phone ? `tel:${lead.phone}` : undefined}
              className="flex items-center gap-2 text-caption text-secondary"
            >
              <i className="fa-solid fa-phone text-[12px] text-tertiary" />
              {lead.phone || "No phone supplied"}
            </a>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto scroll-thin px-[26px] py-[22px] flex flex-col gap-[26px]">
          {editOpen && editValues ? (
            <form
              id="lead-edit-form"
              className="flex flex-col gap-[26px]"
              onSubmit={async (event) => {
                event.preventDefault();
                setSaving(true);
                setEditError("");
                try {
                  await onUpdate?.(lead.id, editValues);
                  setEditOpen(false);
                  setEditValues(null);
                } catch (error) {
                  setEditError(error instanceof Error ? error.message : "Lead update failed");
                } finally {
                  setSaving(false);
                }
              }}
            >
              <div>
                <SectionLabel>Lead details</SectionLabel>
                <div className="grid grid-cols-2 gap-4">
                  <EditField label="First name">
                    <input
                      aria-label="First name"
                      value={editValues.firstName}
                      maxLength={100}
                      onChange={(event) => setEditField("firstName", event.target.value)}
                      className={editInputClass}
                    />
                  </EditField>
                  <EditField label="Last name">
                    <input
                      aria-label="Last name"
                      value={editValues.lastName}
                      maxLength={100}
                      onChange={(event) => setEditField("lastName", event.target.value)}
                      className={editInputClass}
                    />
                  </EditField>
                  <EditField label="Email address" className="col-span-2">
                    <input
                      aria-label="Email address"
                      type="email"
                      required
                      value={editValues.email}
                      maxLength={320}
                      onChange={(event) => setEditField("email", event.target.value)}
                      className={editInputClass}
                    />
                  </EditField>
                  <EditField label="Telephone">
                    <input
                      aria-label="Telephone"
                      type="tel"
                      value={editValues.phone}
                      maxLength={50}
                      onChange={(event) => setEditField("phone", event.target.value)}
                      className={editInputClass}
                    />
                  </EditField>
                  <EditField label="Qualification">
                    <select
                      aria-label="Qualification"
                      value={editValues.status}
                      onChange={(event) =>
                        setEditField("status", event.target.value as UpdateLead["status"])
                      }
                      className={editInputClass}
                    >
                      <option value="MQL">MQL</option>
                      <option value="SQL">SQL</option>
                    </select>
                  </EditField>
                </div>
              </div>

              <div className="border-t border-default pt-[22px]">
                <SectionLabel>Practice</SectionLabel>
                <div className="grid grid-cols-2 gap-4">
                  <EditField label="Practice Name">
                    <input
                      aria-label="Practice Name"
                      value={editValues.practice}
                      maxLength={200}
                      onChange={(event) => setEditField("practice", event.target.value)}
                      className={editInputClass}
                    />
                  </EditField>
                  <EditField label="Practice Postcode">
                    <input
                      aria-label="Practice Postcode"
                      value={editValues.practicePostcode}
                      maxLength={20}
                      onChange={(event) => setEditField("practicePostcode", event.target.value)}
                      className={editInputClass}
                    />
                  </EditField>
                  <EditField label="Practice Address" className="col-span-2">
                    <textarea
                      aria-label="Practice Address"
                      value={editValues.practiceAddress}
                      maxLength={500}
                      rows={3}
                      onChange={(event) => setEditField("practiceAddress", event.target.value)}
                      className={`${editInputClass} h-auto min-h-20 py-2 resize-y`}
                    />
                  </EditField>
                  <EditField label="Practice Type">
                    <input
                      aria-label="Practice Type"
                      value={editValues.practiceType}
                      maxLength={160}
                      onChange={(event) => setEditField("practiceType", event.target.value)}
                      className={editInputClass}
                    />
                  </EditField>
                  <EditField label="Job title">
                    <input
                      aria-label="Job title"
                      value={editValues.jobTitle}
                      maxLength={160}
                      onChange={(event) => setEditField("jobTitle", event.target.value)}
                      className={editInputClass}
                    />
                  </EditField>
                </div>
              </div>

              <div className="border-t border-default pt-[22px]">
                <SectionLabel>Submitted notes</SectionLabel>
                <EditField label="Notes">
                  <textarea
                    aria-label="Submitted notes"
                    value={editValues.enquiryNotes}
                    maxLength={10_000}
                    rows={6}
                    onChange={(event) => setEditField("enquiryNotes", event.target.value)}
                    className={`${editInputClass} h-auto min-h-28 py-2 resize-y`}
                  />
                </EditField>
                <p className="mt-2 text-micro text-tertiary">
                  Team notes and delivery history are managed separately and will not be changed.
                </p>
              </div>
              {editError && (
                <p role="alert" className="text-caption text-status-red">
                  {editError}
                </p>
              )}
            </form>
          ) : null}
          {/* Outcomes — the conversion surface */}
          <div className={editOpen ? "hidden" : ""}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tertiary">
                Outcomes
              </span>
              {wonValue > 0 && (
                <span className="inline-flex items-center gap-1.5 text-caption font-semibold text-status-green bg-[rgba(86,205,174,0.12)] rounded-xs px-2 py-1">
                  <i className="fa-solid fa-trophy text-[11px]" />
                  {money(wonValue)}/yr won
                </span>
              )}
            </div>
            <OutcomeTimeline outcomes={outcomes} />
            {canEditLeads && (
              <OutcomeComposer
                actorName={userName || "Current user"}
                onAdd={(o) => onAddOutcome(lead.id, o)}
              />
            )}
          </div>

          {/* Origin */}
          <div className={`border-t border-default pt-[22px] ${editOpen ? "hidden" : ""}`}>
            <SectionLabel>Origin</SectionLabel>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6">
              <div className="flex flex-col gap-1">
                <span className="text-micro text-tertiary">Source</span>
                <span className="inline-flex items-center gap-2 text-body text-primary">
                  <i
                    className={`${src.brand ? "fa-brands" : "fa-solid"} ${src.icon} text-[15px]`}
                    style={{ color: src.color }}
                  />
                  {src.label}
                </span>
              </div>
              <Field label="Captured" value={lead.capturedAt} />
            </div>
          </div>

          {/* Practice */}
          <div className={`border-t border-default pt-[22px] ${editOpen ? "hidden" : ""}`}>
            <SectionLabel>Practice</SectionLabel>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6">
              <Field label="Practice Name" value={lead.practice || "Not supplied"} />
              <Field label="Practice Postcode" value={lead.practicePostcode || "Not supplied"} />
              <Field label="Practice Address" value={lead.practiceAddress || "Not supplied"} />
              <Field label="Practice Type" value={lead.practiceType || "Not supplied"} />
              {lead.jobTitle && <Field label="Job title" value={lead.jobTitle} />}
            </div>
          </div>

          {/* Delivery */}
          <div className={`border-t border-default pt-[22px] ${editOpen ? "hidden" : ""}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-tertiary">
                Delivery log
              </span>
              <StatusBadge label={del.label} tone={del.tone} />
            </div>
            <div className="flex flex-col">
              {lead.deliveryLog.map((a, i) => {
                const m = resultMeta(a.result);
                return (
                  <div
                    key={i}
                    className={`flex gap-3 ${i > 0 ? "pt-3.5 mt-3.5 border-t border-default" : ""}`}
                  >
                    <i className={`fa-solid ${m.icon} ${m.color} text-[14px] mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-body font-medium text-primary">
                          {a.deliveryKind === "reminder_1"
                            ? "Reminder 1"
                            : a.deliveryKind === "reminder_2"
                              ? "Reminder 2"
                              : a.channel}
                        </span>
                        <span className="text-micro text-tertiary">{a.at}</span>
                      </div>
                      <div className="text-caption text-secondary mt-0.5">{a.detail}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            {lead.delivery === "failed" && (
              <p className="mt-4 text-caption text-tertiary">
                Delivery retries and replays are run by FMC support.
              </p>
            )}
          </div>

          {/* Notes */}
          <div className={`border-t border-default pt-[22px] ${editOpen ? "hidden" : ""}`}>
            <SectionLabel>Notes</SectionLabel>
            <div className="flex flex-col gap-4">
              {lead.enquiryNotes && (
                <div>
                  <div className="text-micro font-semibold uppercase tracking-[0.08em] text-tertiary mb-1.5">
                    Submitted with lead
                  </div>
                  <p className="text-body text-secondary leading-relaxed whitespace-pre-wrap break-words">
                    {lead.enquiryNotes}
                  </p>
                </div>
              )}
              {lead.notes && (
                <div>
                  <div className="text-micro font-semibold uppercase tracking-[0.08em] text-tertiary mb-1.5">
                    Team notes
                  </div>
                  <p className="text-body text-secondary leading-relaxed whitespace-pre-wrap break-words">
                    {lead.notes}
                  </p>
                </div>
              )}
            </div>
            {!lead.enquiryNotes && !lead.notes && (
              <p className="text-body text-tertiary italic">No notes added yet.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-default px-[26px] py-4">
          {editOpen ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setEditOpen(false);
                  setEditValues(null);
                  setEditError("");
                }}
                className="flex-1 h-10 rounded-xs border border-default text-body font-medium text-secondary hover:bg-surface-light transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="lead-edit-form"
                disabled={saving}
                className="flex-1 h-10 rounded-xs bg-brand-red text-on-brand text-body font-medium flex items-center justify-center gap-2 hover:bg-brand-red-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="fa-solid fa-check text-[12px]" />
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          ) : deleteOpen ? (
            <div className="flex flex-col gap-3">
              <div className="rounded-md border border-default bg-brand-red-tint px-3.5 py-3">
                <div className="flex items-start gap-2.5">
                  <i className="fa-solid fa-triangle-exclamation text-status-red text-[14px] mt-0.5" />
                  <p className="text-caption text-primary leading-relaxed">
                    This permanently erases the lead, its outcomes and delivery history from this
                    brand's tenant.
                  </p>
                </div>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-micro text-tertiary">
                  Reason <span className="text-status-red">*</span>
                </span>
                <select
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="h-9 px-3 rounded-xs border border-default bg-surface-white text-caption text-primary"
                >
                  <option value="">Select a reason</option>
                  <option>Data subject erasure request</option>
                  <option>Duplicate record</option>
                  <option>Test or junk data</option>
                  <option>Other</option>
                </select>
              </label>
              <label className="flex items-start gap-2 text-caption text-secondary">
                <input type="checkbox" checked={true} disabled className="mt-0.5" />
                <span>
                  Previously used downstream destinations will be notified without redisclosing PII.
                </span>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-micro text-tertiary">
                  Confirm your password <span className="text-status-red">*</span>
                </span>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  className="h-9 px-3 rounded-xs border border-default bg-surface-white text-caption text-primary"
                />
              </label>
              {deleteError && <p className="text-caption text-status-red">{deleteError}</p>}
              <p className="text-micro text-tertiary">
                This action is written to the audit log against the acting user.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDeleteOpen(false)}
                  className="flex-1 h-9 rounded-xs border border-default text-body font-medium text-secondary hover:bg-surface-light transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={!deleteReason || !password || deleting}
                  onClick={async () => {
                    if (!deleteReason || !password) return;
                    setDeleting(true);
                    setDeleteError("");
                    try {
                      await onDelete?.(lead.id, password);
                      setDeleteOpen(false);
                      onClose();
                    } catch (error) {
                      setDeleteError(error instanceof Error ? error.message : "Erasure failed");
                    } finally {
                      setDeleting(false);
                    }
                  }}
                  className="flex-1 h-9 rounded-xs bg-brand-red text-on-brand text-body font-medium hover:bg-brand-red-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {deleting ? "Erasing…" : "Erase lead"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  const body = window.prompt("Add an encrypted note to this lead");
                  if (body?.trim()) await onAddNote?.(lead.id, body.trim());
                }}
                disabled={!canEditLeads}
                className="flex-1 h-10 px-4 rounded-xs border border-default text-button text-body font-medium flex items-center justify-center gap-2 hover:bg-surface-light transition-colors disabled:opacity-40"
              >
                <i className="fa-solid fa-pen text-[13px]" />
                Add note
              </button>
              {canEditLeads && (
                <button
                  onClick={() => setDeleteOpen(true)}
                  className="h-10 px-3 rounded-xs border border-default text-status-red text-body font-medium hover:bg-brand-red-tint transition-colors"
                >
                  <i className="fa-solid fa-trash text-[13px]" />
                  <span className="sr-only">Delete lead</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
